import { combine, createEvent, createStore, sample } from 'effector'
import { produce } from 'immer'
import mapValues from 'lodash/mapValues'
import { interval, spread } from 'patronum'

import * as chat from '@/entities/chat'
import { env } from '@/shared/config'
import { filterMapValues } from '@/shared/lib/types'

import * as channelModel from './model.channel'
import * as connModel from './model.conn'
import * as modelMessage from './model.message'
import * as userModel from './model.user'

export const $cooldownSecondsByChannel = createStore<Record<string, number | null>>({})
export const $slowmodeSecondsLeftByChannel = createStore<Record<string, number>>({})

export const $channelSlowmodeCooldown = combine(
    [$slowmodeSecondsLeftByChannel, channelModel.$chatChannel],
    ([cooldown, channel]) => cooldown[channel] ?? null
)

export const userMessageReceived = sample({
    clock: connModel.chatMessageReceived,
    source: chat.$userInfo,
    filter: (userInfo, { event }) => event.payload.user_id === userInfo?.user_id,
    fn: (_, event) => event,
})

// sync slowmode config on login
sample({
    clock: userModel.loginFx.doneData,
    filter: ({ slowmode }) => slowmode !== undefined,
    fn: ({ slowmode }) => ({
        cooldownSecondsMap: mapValues(slowmode!, (slowmode) => slowmode.cooldown_seconds),
        timeLeftMap: filterMapValues(slowmode!, (slowmode) => slowmode.cooldown_left),
    }),
    target: spread({
        targets: {
            cooldownSecondsMap: $cooldownSecondsByChannel,
            timeLeftMap: $slowmodeSecondsLeftByChannel,
        },
    }),
})

const { tick } = interval({
    timeout: 1000,
    // NOTE: calling loginFx from tests should not trigger interval timer
    start: env.IS_TEST ? createEvent() : userModel.loginFx.done,
})

// countdown timer
sample({
    clock: tick,
    source: $slowmodeSecondsLeftByChannel,
    fn: produce((timeLeftMap: Record<string, number>) => {
        for (const [channel, timeLeft] of Object.entries(timeLeftMap)) {
            const newTimeLeft = timeLeft - 1
            if (newTimeLeft > 0) {
                timeLeftMap[channel] = newTimeLeft
            } else {
                delete timeLeftMap[channel]
            }
        }
    }),
    target: $slowmodeSecondsLeftByChannel,
})

// adjust timer on slowmode updates
sample({
    clock: connModel.slowmodeUpdated,
    source: {
        timeLeftMap: $slowmodeSecondsLeftByChannel,
        cooldownMap: $cooldownSecondsByChannel,
        userInfo: chat.$userInfo,
    },
    // NOTE: privileged users can send messages without cooldown
    filter: ({ userInfo }) => !chat.isPrivilegedUser(userInfo),
    fn: ({ timeLeftMap, cooldownMap }, { channel, cooldown_seconds: newCooldown }) =>
        produce(timeLeftMap, (timeLeftMap) => {
            if (newCooldown === null) {
                // If the new cooldown is null, remove channel timer
                delete timeLeftMap[channel]
                return
            }

            const oldCooldown = cooldownMap[channel]
            const secondsLeft = timeLeftMap[channel]
            if (!oldCooldown || !secondsLeft) {
                return
            }

            const cooldownDiff = newCooldown - oldCooldown
            const newTimeLeft = secondsLeft + cooldownDiff
            if (newTimeLeft <= 0) {
                // If the new cooldown is less than the time left, remove channel timer
                delete timeLeftMap[channel]
                return
            }

            timeLeftMap[channel] = newTimeLeft
        }),
    target: $slowmodeSecondsLeftByChannel,
})

// update cooldown map on slowmode updates
sample({
    clock: connModel.slowmodeUpdated,
    source: $cooldownSecondsByChannel,
    fn: (cooldownMap, { channel, cooldown_seconds: newCooldown }) => ({
        ...cooldownMap,
        [channel]: newCooldown,
    }),
    target: $cooldownSecondsByChannel,
})

// adjust cooldown timer on new message
sample({
    // process optimistic message to start cooldown timer immediately
    clock: modelMessage.messageSendStatus.allowed,
    source: {
        timeLeftMap: $slowmodeSecondsLeftByChannel,
        cooldownMap: $cooldownSecondsByChannel,
        userInfo: chat.$userInfo,
    },
    // NOTE: privileged users can send messages without cooldown
    filter: ({ userInfo }) => !chat.isPrivilegedUser(userInfo),
    fn: ({ timeLeftMap, cooldownMap }, { channel }) =>
        produce(timeLeftMap, (timeLeftMap) => {
            const cooldown = cooldownMap[channel]
            if (cooldown) {
                timeLeftMap[channel] = cooldown
            }
        }),

    target: $slowmodeSecondsLeftByChannel,
})
